// Angular imports
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';

// Service imports
import { AuthService } from '../../_services/auth.service';
import { ServerErrorService } from '../../_services/shared-services/server-error.service';
import { HelperService } from '../../_services/shared-services/helper.service';
import { PasswordGeneratorService } from '../../_services/shared-services/password-generator.service';
import { QuickPayAuthService } from '../quick-payment/services/quick-pay-auth.service';

// Model imports
import { GoogleRegistrationResponseDto } from '../../_models/google-registration-response-dto';

// Constant imports
import {
  QuickPayStorageKeys,
  QuickPayErrorMessages,
  QuickPayErrorCodes
} from '../quick-payment/constants/quick-pay.constants';

// CC-Common imports
import { RxjsUtils } from '@therealmanage/ciranet-ui-common';

@Component({
  selector: 'app-google-callback',
  templateUrl: './google-callback.component.html',
  styleUrls: ['./google-callback.component.scss'],
  standalone: false
})
export class GoogleCallbackComponent implements OnInit, OnDestroy {
  loadingVisible = true;
  errorMessage: string | null = null;

  private readonly GOOGLE_REGISTER_DATA_KEY = 'google_register_data';
  private readonly GOOGLE_REGISTRATION_RESULT_KEY =
    'google_registration_result';
  private readonly ERROR_REDIRECT_DELAY_MS = 5000;
  private subscriptions = new Subscription();
  private redirectTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private errorService: ServerErrorService,
    private helperService: HelperService,
    private passwordGenerator: PasswordGeneratorService,
    private quickPayAuth: QuickPayAuthService
  ) {}

  ngOnInit(): void {
    // Check if this is a registration callback
    const isRegistration = this.isRegistrationCallback();

    this.subscriptions.add(
      RxjsUtils.subscribeToQueryParams(this.route, (params) => {
        const code = params['code'];
        const error = params['error'];
        const errorReason = params['reason'];
        const errorMessage = params['message'];

        if (error) {
          this.handleError(error, errorReason, errorMessage, isRegistration);
        } else if (code) {
          if (isRegistration) {
            this.handleRegistrationCallback(code);
          } else {
            this.exchangeCodeForToken(code);
          }
        } else {
          this.handleError(
            'invalid_callback',
            'No code or error provided',
            'Invalid callback from Google authentication',
            isRegistration
          );
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    if (this.redirectTimeout) {
      clearTimeout(this.redirectTimeout);
    }
  }

  private isRegistrationCallback(): boolean {
    const registerData = sessionStorage.getItem(this.GOOGLE_REGISTER_DATA_KEY);
    return !!registerData;
  }

  private handleRegistrationCallback(code: string): void {
    RxjsUtils.safeSubscribe(
      this.authService.getGoogleRegistrationInfo(code),
      (response: GoogleRegistrationResponseDto) => {
        this.processRegistrationResponse(response);
      },
      (error) => {
        this.loadingVisible = false;
        const message =
          error?.error?.Message || 'Failed to complete Google registration';
        this.handleError(
          'registration_failed',
          'Registration failed',
          message,
          true
        );
      }
    );
  }

  private processRegistrationResponse(
    response: GoogleRegistrationResponseDto
  ): void {
    const registerData = sessionStorage.getItem(this.GOOGLE_REGISTER_DATA_KEY);
    if (!registerData) {
      this.handleError(
        'missing_data',
        'Missing registration data',
        'Registration data not found. Please start again.',
        true
      );
      return;
    }

    let parsedData: {
      customerID?: string;
      checkDigit?: string;
      recaptchaToken?: string;
      contactInfo?: any;
      origin?: string;
    };
    try {
      parsedData = JSON.parse(registerData);
    } catch {
      this.handleError(
        'invalid_data',
        'Invalid registration data',
        'Registration data is corrupted. Please start again.',
        true
      );
      return;
    }

    const { customerID, checkDigit, recaptchaToken, contactInfo, origin } =
      parsedData;

    if (!customerID || !checkDigit) {
      this.handleError(
        'incomplete_data',
        'Incomplete registration data',
        'Required registration data is missing. Please start again.',
        true
      );
      return;
    }

    // Generate password and store result for registration completion
    const registrationResult = {
      email: response.email,
      givenName: response.givenName,
      familyName: response.familyName,
      fullName: response.fullName,
      googleId: response.googleId,
      generatedPassword: this.passwordGenerator.generatePassword(),
      customerID,
      checkDigit,
      recaptchaToken,
      contactInfo
    };

    sessionStorage.setItem(
      this.GOOGLE_REGISTRATION_RESULT_KEY,
      JSON.stringify(registrationResult)
    );

    // Cleanup the initial register data
    sessionStorage.removeItem(this.GOOGLE_REGISTER_DATA_KEY);

    // Navigate based on where the user started
    if (origin === 'signup') {
      // User started from user/signup page
      this.router.navigate(['user', 'signup'], {
        queryParams: { fromGoogle: 'true' }
      });
    } else {
      // User started from login page registration panel (default)
      this.router.navigate(['user', 'login'], {
        queryParams: { fromGoogle: 'true' }
      });
    }
  }

  private exchangeCodeForToken(code: string): void {
    // Check if this is a Quick Pay flow before exchanging code
    const isQuickPayFlow = this.isQuickPayCallback();

    RxjsUtils.safeSubscribe(
      this.authService.exchangeGoogleCode(code),
      () => {
        if (isQuickPayFlow) {
          // Quick Pay flow: fetch properties, clear auth, then navigate
          this.handleQuickPayFlow();
        } else {
          // Standard login flow
          this.loadingVisible = false;
          this.authService.updateSubjectLoginSuccess(true);
          this.navigateAfterLoginSuccess();
        }
      },
      (error) => {
        this.loadingVisible = false;
        const message =
          error?.error?.Message || 'Failed to complete Google authentication';
        this.handleError(
          'token_exchange_failed',
          'Token exchange failed',
          message,
          false,
          isQuickPayFlow
        );
      }
    );
  }

  /**
   * Handles the Quick Pay Google OAuth flow:
   * 1. Fetch user's properties while authenticated
   * 2. Clear auth state (user should not remain logged in)
   * 3. If properties found: store in session storage and navigate to Quick Pay
   * 4. If no properties: redirect to login page with error
   *
   * This ensures the user is never "logged in" when arriving at Quick Pay,
   * preventing the authenticated dashboard layout from showing.
   */
  private handleQuickPayFlow(): void {
    RxjsUtils.safeSubscribe(
      this.quickPayAuth.fetchAuthenticatedUserProperties(),
      (properties) => {
        // Auth state is already cleared by fetchAuthenticatedUserProperties()
        // Clear the Google OAuth flag
        sessionStorage.removeItem(QuickPayStorageKeys.GOOGLE_AUTH_FLAG);
        this.loadingVisible = false;

        if (properties.length === 0) {
          // No properties - redirect to login page with error
          // Quick Pay component on login page will display the error
          this.router.navigate(['user', 'login'], {
            queryParams: { quickPayError: QuickPayErrorCodes.NO_PROPERTIES }
          });
          return;
        }

        // Store properties for Quick Pay to consume
        sessionStorage.setItem(
          QuickPayStorageKeys.GOOGLE_AUTH_PROPERTIES,
          JSON.stringify(properties)
        );

        // Navigate to login page with flag to auto-expand Quick Pay card
        // The embedded Quick Pay component will read the properties from session storage
        this.router.navigate(['user', 'login'], {
          queryParams: { quickPaySuccess: 'true' }
        });
      },
      (error) => {
        // Auth state is already cleared by fetchAuthenticatedUserProperties() on error
        sessionStorage.removeItem(QuickPayStorageKeys.GOOGLE_AUTH_FLAG);
        this.loadingVisible = false;

        const message =
          error?.error?.message ||
          QuickPayErrorMessages.UNABLE_TO_RETRIEVE_PROPERTIES;
        this.handleError(
          'property_fetch_failed',
          'Failed to fetch properties',
          message,
          false,
          true
        );
      }
    );
  }

  /**
   * Check if this callback is from Quick Pay Google OAuth flow.
   * Quick Pay sets a session flag before initiating OAuth.
   */
  private isQuickPayCallback(): boolean {
    return (
      sessionStorage.getItem(QuickPayStorageKeys.GOOGLE_AUTH_FLAG) === 'true'
    );
  }

  private handleError(
    errorCode: string,
    reason: string,
    message: string,
    isRegistration: boolean = false,
    isQuickPayFlow: boolean = false
  ): void {
    this.loadingVisible = false;
    this.errorMessage = this.getFriendlyErrorMessage(
      errorCode,
      reason,
      message
    );

    // Show error to user (skip for Quick Pay - it shows its own error)
    if (!isQuickPayFlow) {
      this.errorService.updateSubjectErrorMessage(this.errorMessage);
    }

    // Cleanup sessionStorage on error
    if (isRegistration) {
      sessionStorage.removeItem(this.GOOGLE_REGISTER_DATA_KEY);
    }

    // Redirect after a delay
    this.redirectTimeout = setTimeout(() => {
      if (isQuickPayFlow) {
        // For Quick Pay, redirect to login page with error indicator
        // This auto-expands the Quick Pay card where the embedded component shows the error
        this.router.navigate(['user', 'login'], {
          queryParams: { quickPayError: QuickPayErrorCodes.GOOGLE_SSO_FAILED }
        });
      } else {
        this.router.navigate(['user', 'login']);
      }
    }, this.ERROR_REDIRECT_DELAY_MS);
  }

  private getFriendlyErrorMessage(
    errorCode: string,
    reason: string,
    message: string
  ): string {
    // Map backend error codes to user-friendly messages
    const errorMessages: { [key: string]: string } = {
      oauth_error:
        'Google authentication was cancelled or denied. Please try again.',
      no_email:
        'Unable to retrieve your email from Google. Please ensure email permission is granted.',
      user_not_found:
        'No account found with this Google account. Please register first or use a different sign-in method.',
      user_exists:
        'An account with this email already exists. Please sign in with your password or use a different email.',
      code_generation_failed: 'Authentication failed. Please try again.',
      creation_failed:
        'Failed to create your account. Please try again or contact support.',
      authentication_failed: 'Authentication failed. Please try again.',
      invalid_code: 'Invalid or expired authentication code. Please try again.',
      token_exchange_failed: 'Failed to complete sign-in. Please try again.'
    };

    return (
      errorMessages[errorCode] ||
      message ||
      'An error occurred during Google authentication. Please try again.'
    );
  }

  private navigateAfterLoginSuccess(): void {
    // Standard login flow - Quick Pay uses handleQuickPayFlow() instead
    const returnUrl = this.route.snapshot.queryParams['returnUrl'];
    if (returnUrl) {
      this.router.navigateByUrl(returnUrl);
      this.helperService.subjectAutoSelectHomeMenuItem.next(returnUrl);
    } else {
      this.router.navigate(['/home']);
      this.helperService.subjectAutoSelectHomeMenuItem.next('/home');
    }
  }
}
